import { isSmartTV, isBrowser, isMobile, isTablet } from "react-device-detect";
import moment from "moment";

import i18n, { navigatorLanguage } from "../../i18n";
import {
  GET_SMARTLISTS,
  GET_PURCHASES_VIDEOS,
  GET_SUBSCRIPTIONS_VIDEOS,
} from "actions/ActionTypes";
import rootURL from "utils/RootURL";
import { handleErrors, formatHeaders } from "utils/Api";
import { PROJECTS_ROUTE } from "../../components/routes/routes";
import { getStorefrontUrl } from "./Project";
import { getDefaultSlug } from "../Translations";
import { getMediaEmbed } from "../Content";

export const getIcon = (content) => {
  switch (content?.type) {
    case "hosted":
      if (content.audio_only) {
        return "music_note";
      }
      if (content.is_replay) {
        return "replay";
      }
      return "videocam";

    case "live":
      return "live_tv";
    case "external":
      return "movie";
    default:
      return "videocam";
  }
};

export const getType = (icon) => {
  if (!icon) return null;

  switch (icon) {
    case "auto_awesome_motion":
      return "master";
    case "videocam":
      return "hosted";
    case "music_note":
      return "audio";
    case "replay":
      return "replay";
    case "live_tv":
      return "live";
    case "movie":
      return "external";
    default:
      return "hosted";
  }
};

export const isAudioContent = (content) => {
  const contentType = getIcon(content);
  return ["music_note"].includes(contentType);
};

export const getFreeType = (content, freecontents) => {
  const freecontent = freecontents.data.find((freecontent) => {
    if (freecontent.media_uuid === content.uuid) {
      return freecontent.type;
    }
  });

  return freecontent.type;
};

export const isFree = (content, freecontents, type) => {
  let mediaFree = false;

  freecontents.data.forEach((freecontent) => {
    if (
      freecontent.media_uuid === content.uuid &&
      (!type || freecontent.type === type)
    )
      mediaFree = true;
  });

  return mediaFree;
};

export const isAudioOnly = (uuid, contents) => {
  if (uuid) {
    const content = getElementByUuid(uuid, contents);
    if (content && content.audio_only) return true;
  }

  return false;
};

export const getLanguage = (language_uuid, languages) => {
  if (typeof language_uuid !== "string") return null;

  for (let i = 0; i < languages.length; ++i) {
    if (languages[i].value === language_uuid) return languages[i];
  }

  return null;
};

export const getLanguageByUuid = (language_uuid, languages) => {
  if (typeof language_uuid !== "string") return null;

  for (let i = 0; i < languages.length; ++i) {
    if (languages[i].uuid === language_uuid) return languages[i];
  }

  return null;
};

export const getLanguageUuid = (languages, language) => {
  let language_uuid = null;
  const language_uuid_index = {};
  languages.data.forEach((it) => {
    if (it.language === language) {
      language_uuid = it.uuid;
      language_uuid_index[language_uuid] = [0];
    }
  });
  return { language_uuid, language_uuid_index };
};

export const getDefaultProjectLanguage = (languages, project) => {
  let language = navigatorLanguage;
  if (project && project.translations) {
    project.translations.forEach((it) => {
      if (it.default) {
        language = it.language;
      }
    });
  }

  let ret = null;
  if (languages) {
    const { language_uuid, language_uuid_index } = getLanguageUuid(
      languages,
      language,
    );
    ret = {
      language,
      language_uuid,
      language_uuid_index,
    };
  } else {
    const language_index = {};
    language_index[language] = [0];
    ret = {
      language,
      language_index,
    };
  }
  return ret;
};

export const getDurationString = ({ duration, isShort }) => {
  if (!duration)
    return isShort
      ? i18n.t("common:ssShort", { count: 0 })
      : i18n.t("common:ss", { count: 0 });

  const momentDuration = moment.duration(duration, "seconds");

  const days = Math.floor(momentDuration.asDays());
  const hours = momentDuration.hours();
  const minutes = momentDuration.minutes();
  const seconds = momentDuration.seconds();

  const daysString = isShort
    ? i18n.t("common:ddShort", { count: days })
    : i18n.t("common:dd", { count: days });
  const hourString = isShort
    ? i18n.t("common:hhShort", { count: hours })
    : i18n.t("common:hh", { count: hours });
  const minuteString = isShort
    ? i18n.t("common:mmShort", { count: minutes })
    : i18n.t("common:mm", { count: minutes });
  const secondString = isShort
    ? i18n.t("common:ssShort", { count: seconds })
    : i18n.t("common:ss", { count: seconds });

  return `
    ${days ? daysString : ""}
    ${hours ? hourString : ""}
    ${minutes ? minuteString : ""}
    ${seconds ? secondString : ""}
  `;
};

export const getDurationShort = (duration) => {
  const mDuration = moment.duration(duration, "seconds");
  const format = duration > 3600 ? "hh:mm:ss" : "mm:ss";

  return mDuration.format(format, { trim: false });
};

export const isMediaPlayable = (
  media,
  subscriptions_videos,
  purchases_videos,
  isLoggedIn,
) => {
  if (!media) return false;

  for (let i = 0; i < subscriptions_videos.length; i++) {
    if (subscriptions_videos[i].uuid === media.uuid) return true;
  }
  for (let i = 0; i < purchases_videos.length; i++) {
    if (purchases_videos[i].uuid === media.uuid) return true;
  }

  if (media.free && media.free_type === "free") return true;

  if (media.free && media.free_type === "free_registered" && isLoggedIn)
    return true;

  return false;
};

export const hasSVODOffer = (media, offers) => {
  let isSvod = false;
  if (media.offers) {
    media.offers.forEach((o) => {
      const offer = getElementByUuid(o.uuid, offers);
      if (offer && offer.offer_plans && offer.offer_plans.length > 0) {
        if (offer.offer_plans[0].frequency > 0) isSvod = true;
      }
    });
  }

  return isSvod;
};

export const hasBundleOffer = (media, offers) => {
  let isBundle = false;
  if (media.offers) {
    media.offers.forEach((o) => {
      const offer = getElementByUuid(o.uuid, offers);
      if (offer && offer.offer_plans && offer.offer_plans.length > 0) {
        if (offer.offer_plans[0].rental_duration > 0) isBundle = true;
      }
    });
  }

  return isBundle;
};

export const getSerieFromSeason = (seasonUuid, smartlists) => {
  let serie = null;
  let seasonIdx = null;

  if (smartlists?.data) {
    smartlists.data.forEach((it) => {
      if (it.type === "series" && it.smartlist_items.length > 0) {
        it.smartlist_items.forEach((season) => {
          if (season.content_uuid === seasonUuid) {
            seasonIdx = season.rank;
            serie = it;
          }
        });
      }
    });
  }

  return { serie, seasonIdx };
};

export const getSeasonFromEpisode = (episodeUuid, smartlists) => {
  let season = null;
  let episodeIdx = null;

  if (smartlists?.data) {
    smartlists.data.forEach((it) => {
      if (it.type === "season" && it.smartlist_items.length > 0) {
        it.smartlist_items.forEach((episode) => {
          if (
            episode.content_uuid === episodeUuid &&
            getSerieFromSeason(it.uuid, smartlists).serie !== null
          ) {
            episodeIdx = episode.rank;
            season = it;
          }
        });
      }
    });
  }

  return { season, episodeIdx };
};

const getRelatedContents = (
  params,
  contents,
  castings,
  casting_type,
  title_trad,
) => {
  const ret = [];
  let castingName = "";

  if (!params.casting_uuid) return { contents: ret, name: "" };

  if (castings && castings.data) {
    castings.data.forEach((cast) => {
      if (cast.uuid === params.casting_uuid) {
        castingName = getTranslatedAttr(cast, "name");
      }
    });
  }

  contents.data.forEach((content) => {
    if (
      content.medias_castings_indexes.type[casting_type] &&
      content.medias_castings_indexes.type[casting_type].length > 0
    ) {
      content.medias_castings_indexes.type[casting_type].forEach((index) => {
        if (
          content.medias_castings[index].casting_uuid === params.casting_uuid
        ) {
          ret.push(content);
        }
      });
    }
  });

  return {
    contents: ret,
    name: i18n.t(`content:titleRelatedContentsAs${title_trad}`, {
      casting: castingName,
    }),
  };
};

const getRelatedContentsAsActor = (params, contents, castings) =>
  getRelatedContents(params, contents, castings, "actor", "Actor");
const getRelatedContentsAsDirector = (params, contents, castings) =>
  getRelatedContents(params, contents, castings, "director", "Director");
const getRelatedContentsAsProducer = (params, contents, castings) =>
  getRelatedContents(params, contents, castings, "producer", "Producer");
const getRelatedContentsAsAuthor = (params, contents, castings) =>
  getRelatedContents(params, contents, castings, "author", "Author");

let fetchingSimilarContent = false; // To limit one recommended api call

const getSimilarContents = (params, contents) => {
  const content_uuid = params.content_uuid ? params.content_uuid : params.uuid;
  const endpoint = rootURL(`/api/search/v2/medias/${content_uuid}/recommended`);
  const data = {
    page: 1,
    per_page: 15,
  };

  const ret = [];
  if (!fetchingSimilarContent) {
    fetchingSimilarContent = true;
    return fetch(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    })
      .then(handleErrors)
      .then((similarContents) => {
        if (similarContents && similarContents.data) {
          similarContents.data.forEach((uuid) => {
            const content = getElementByUuid(uuid, contents);
            if (content) ret.push(content);
          });
        }

        fetchingSimilarContent = false;
        return { contents: ret, name: i18n.t("content:titleSimilarContents") };
      })
      .catch((err) => {
        console.error(err);
        fetchingSimilarContent = false;
        return { contents: [], name: i18n.t("content:titleSimilarContents") };
      });
  }
  return null;
};

export const getPlaylistContents = (
  contents,
  my_playlist,
  type,
  isEduProject,
) => {
  const ret = [];
  let trans = null;

  if (my_playlist && my_playlist.medias && !isEduProject) {
    my_playlist.medias.forEach((item) => {
      const content = getElementByUuid(item.media_uuid, contents);

      if (content) ret.push(content);
    });
  } else if (my_playlist && my_playlist.length && !isEduProject) {
    my_playlist.forEach((item) => {
      const content = getElementByUuid(item.media_uuid, contents);

      if (content) ret.push(content);
    });
  }

  if (type === "my_list") {
    trans = translationsFromKey("content:titleMyList", [
      "ar",
      "en",
      "fr",
      "co",
      "de",
      "es",
      "it",
      "ja",
      "pt",
      "nl",
    ]);
  } else if (type === "my_playlists") {
    trans = translationsFromKey(null, [
      "ar",
      "en",
      "fr",
      "co",
      "de",
      "es",
      "it",
      "ja",
      "pt",
      "nl",
    ]);
  }

  return {
    contents: ret,
    translations: trans.translations,
    translations_indexes: trans.translations_indexes,
    type,
  };
};

export const getTotalDuration = (medias) => {
  let total_duration = 0;

  if (!medias) return total_duration;

  medias.forEach((media) => {
    if (media.duration) total_duration += media.duration;
  });

  return total_duration;
};

export const getResumeContents = (contents, resume_medias, isEduProject) => {
  const ret = [];

  if (resume_medias && resume_medias.data && !isEduProject) {
    resume_medias.data.forEach((item) => {
      const uuid = Object.keys(item)[0];
      const timestamp = item[uuid];

      const content = getElementByUuid(uuid, contents);
      if (content) {
        content.resume_timestamp = timestamp;
        ret.push(content);
      }
    });
  }

  const trans = translationsFromKey("content:titleResumeMedias", [
    "ar",
    "en",
    "fr",
    "co",
    "de",
    "es",
    "it",
    "ja",
    "pt",
    "nl",
  ]);

  return {
    contents: ret,
    translations: trans.translations,
    translations_indexes: trans.translations_indexes,
    type: "resume",
  };
};

const dynamicSmartlists = {
  related_contents_as_actor: getRelatedContentsAsActor,
  related_contents_as_director: getRelatedContentsAsDirector,
  related_contents_as_producer: getRelatedContentsAsProducer,
  related_contents_as_author: getRelatedContentsAsAuthor,
  similar_contents: getSimilarContents,
  my_list: getPlaylistContents,
  my_playlists: getPlaylistContents,
  resume: getResumeContents,
};

export const isDynamicSmartlist = (uuid) => dynamicSmartlists[uuid] != null;

const getDynamicSmartlistContents = (
  smartlist_uuid,
  contents,
  dynamicParams,
  castings,
  my_list,
  resume_medias,
  isEduProject,
) => {
  switch (smartlist_uuid) {
    case "related_contents_as_actor":
    case "related_contents_as_director":
    case "related_contents_as_producer":
    case "related_contents_as_author":
      return dynamicSmartlists[smartlist_uuid](
        dynamicParams,
        contents,
        castings,
      );
    case "similar_contents":
      return dynamicSmartlists[smartlist_uuid](dynamicParams, contents);
    case "my_list":
      return dynamicSmartlists[smartlist_uuid](
        contents,
        my_list,
        "my_list",
        isEduProject,
      );
    case "resume":
      return dynamicSmartlists[smartlist_uuid](
        contents,
        resume_medias,
        isEduProject,
      );
  }
};

export const getSmartlistContents = (
  smartlist_uuid,
  smartlists,
  contents,
  displays,
  dynamicParams = {},
  castings,
  my_list,
  resume_medias,
  isEduProject,
) => {
  const ret = [];

  if (contents.data.length <= 0 && displays.data.length <= 0)
    return { contents: ret };

  if (isDynamicSmartlist(smartlist_uuid))
    return getDynamicSmartlistContents(
      smartlist_uuid,
      contents,
      dynamicParams,
      castings,
      my_list,
      resume_medias,
      isEduProject,
    );

  const smartlist = getElementByUuid(smartlist_uuid, smartlists);

  if (!smartlist) return { contents: ret };

  smartlist.smartlist_items.forEach((item) => {
    let datas = contents;
    if (item.content_type === "smartlist") datas = smartlists;
    if (item.content_type === "display") datas = displays;
    if (item.content_type === "casting") datas = castings;

    const elt = getElementByUuid(item.content_uuid, datas);

    if (elt) {
      ret.push({
        ...elt,
        content_type: item.content_type,
        rank: item.rank,
        smartlist_uuid,
      });
    }
  });

  return {
    contents: ret,
    translations: smartlist.translations,
    translations_indexes: smartlist.translations_indexes,
    type: smartlist.type,
  };
};

export const getCastingUuids = (contents) => {
  let casting_uuids = [];
  contents.forEach((content) => {
    if (content.medias_castings.length > 0) {
      casting_uuids = casting_uuids.concat(
        content.medias_castings.map(
          (media_casting) => media_casting.casting_uuid,
        ),
      );
    }
  });

  return casting_uuids.filter(
    (uuid, index, self) => self.indexOf(uuid) === index,
  );
};

export const getContentUuidsFromCategories = (categories) => {
  let content_uuids = [];
  categories.forEach((category) => {
    if (category.videos.length > 0) {
      content_uuids = content_uuids.concat(
        category.videos.map((video) => video.uuid),
      );
    }
  });
  return content_uuids.filter(
    (uuid, index, self) => self.indexOf(uuid) === index,
  );
};

export const getContentUuidsFromSmartlists = (smartlists) => {
  const content_uuids = [];

  smartlists.forEach((smartlist) => {
    if (smartlist.smartlist_items.length > 0) {
      smartlist.smartlist_items.forEach((item) => {
        if (item.content_type === "video")
          content_uuids.push(item.content_uuid);
      });
    }
  });

  return content_uuids.filter(
    (uuid, index, self) => self.indexOf(uuid) === index,
  );
};

export const concatIndexes = (indexes, newIndexes, n) => {
  const ret = {};

  // Update the positions of the new newIndexes
  for (const key in newIndexes) {
    if (Object.prototype.hasOwnProperty.call(newIndexes, key)) {
      for (const i in newIndexes[key]) {
        newIndexes[key][i] = newIndexes[key][i].map((index) => index + n);
      }
    }
  }

  // Merge the indices
  for (const key in indexes) {
    ret[key] = {};
    if (newIndexes[key]) {
      for (const i in indexes[key]) {
        if (newIndexes[key][i]) {
          ret[key][i] = indexes[key][i].concat(newIndexes[key][i]);
        } else {
          ret[key][i] = indexes[key][i];
        }
      }
      for (const i in newIndexes[key]) {
        if (!ret[key][i]) ret[key][i] = newIndexes[key][i];
      }
    } else {
      ret[key] = indexes[key];
    }
  }

  // Add the new Indices
  for (const key in newIndexes) {
    if (!ret[key]) ret[key] = newIndexes[key];
  }

  return ret;
};

export const getContentPayload = (
  page,
  per_page,
  action,
  uuids = null,
  item = "contents",
) => {
  const payload = {
    endpoint: "/api/media/v6/medias",
    type: action,
    item,
    data: {
      page,
      per_page,
      lang: i18n.language || i18n.options.fallbackLng[0],
    },
  };

  if (uuids) payload.data.uuids = uuids;

  return payload;
};

export const getSmartlistsPayload = (page, per_page) => ({
  endpoint: "/api/smartlist/v3/smartlists",
  type: GET_SMARTLISTS,
  item: "smartlists",
  data: {
    indexes: ["uuid"],
    nested: {
      translations: {
        attributes: [
          "uuid",
          "pictures",
          "default",
          "language",
          "name",
          "description",
        ],
      },
      smartlist_items: {
        attributes: ["content_type", "content_uuid", "rank"],
      },
      comments: {
        attributes: ["uuid"],
      },
    },
    page,
    per_page,
    lang: i18n.language || i18n.options.fallbackLng[0],
  },
});

export const getSubscriptionsVideosPayload = (token) => ({
  endpoint: "/api/offer/v4/user/subscriptions/medias",
  type: GET_SUBSCRIPTIONS_VIDEOS,
  item: "subscriptions_videos",
  token,
});

export const getPurchasesVideosPayload = (token) => ({
  endpoint: "/api/offer/v4/user/purchases/medias",
  type: GET_PURCHASES_VIDEOS,
  item: "purchases_videos",
  token,
});

export const getTranslation = (item, language) => {
  if (
    !item ||
    !item.translations_indexes ||
    !item.translations ||
    item.translations?.length === 0
  ) {
    return null;
  }

  const lang = language || i18n.language || i18n.options.fallbackLng[0];
  const lang_index = item.translations_indexes.language[lang]
    ? item.translations_indexes.language[lang][0]
    : item.translations_indexes.default.true[0];
  const lang_default = item.translations_indexes.default.true[0];
  const trans = item.translations[lang_index];
  const trans_default = item.translations[lang_default];

  return trans || trans_default;
};

/**
 *  Get image from pictures structure from given ratio and by component type
 *  componentType: enum('smartlist', 'content', 'carousel', 'carousel_n1', 'mosaic', 'casting', 'offer')
 * */
export const getImageFromPictures = (pictures, ratio, componentType) => {
  let image = null;
  if (pictures) {
    switch (ratio) {
      case "169":
        image = pictures.cover_picture_16_9;
        break;
      case "166":
        image = pictures.cover_picture_16_6;
        break;
      case "34":
        image = pictures.cover_picture_3_4;
        break;
      default:
        image = pictures.cover_picture_1_1;
        break;
    }
  }

  if (image && (image.low || image.medium || image.high)) {
    const { low, medium, high } = image;

    image = isBrowser || isSmartTV ? medium : low;
    switch (componentType) {
      case "carousel_n2":
      case "carousel_n3":
      case "carousel_n4":
      case "dialog":
        if (isBrowser || isSmartTV || isMobile || isTablet) image = medium;
        break;
      case "carousel_n1":
      case "smartlist":
      case "content":
      case "offer":
        if (isBrowser || isSmartTV) image = high;
        if (isMobile || isTablet) image = medium;
        break;
      case "cast":
      case "player":
        image = high;
        break;
    }
  }

  return image;
};

/**
 *  componentType: enum('smartlist', 'content', 'carousel_n2', 'mosaic', 'casting')
 *  componentParam: dans le cas de carousel c'est le nombre des vignettes
 * */
export const getTranslatedImage = (item, ratio, componentType, language) => {
  if (!item || item.type === "dialog") return null;

  const lang = language || i18n.language || i18n.options.fallbackLng[0];
  const lang_index = item.translations_indexes.language[lang]
    ? item.translations_indexes.language[lang][0]
    : item.translations_indexes.default.true[0];
  const lang_default = item.translations_indexes.default.true[0];
  const trans = item.translations[lang_index];
  const trans_default = item.translations[lang_default];

  const pictures = trans.pictures || trans_default.pictures;
  return getImageFromPictures(pictures, ratio, componentType);
};

/**
 * Adaptation for new api format with language_uuid
 *  componentType: enum('smartlist', 'content', 'carousel', 'carousel_n1', 'mosaic', 'casting', 'offer')
 *  componentParam: dans le cas de carousel c'est le nombre des vignettes
 * */
export const getTranslatedImageV2 = (
  item,
  ratio,
  componentType,
  language,
  languages,
) => {
  if (!item || item.type === "dialog") return null;

  const lang = language || i18n.language || i18n.options.fallbackLng[0];
  let lang_uuid = null;

  languages.data.forEach((it) => {
    if (it.language === lang) {
      lang_uuid = it.uuid;
    }
  });

  const lang_index = item.translations_indexes.language_uuid[lang_uuid]
    ? item.translations_indexes.language_uuid[lang_uuid][0]
    : item.translations_indexes.default.true[0];
  const lang_default = item.translations_indexes.default.true[0];
  const trans = item.translations[lang_index];
  const trans_default = item.translations[lang_default];

  const pictures = trans.pictures || trans_default.pictures;
  return getImageFromPictures(pictures, ratio, componentType);
};

export const getTranslatedAttr = (item, attr, language) => {
  if (!item || item.type === "dialog") return null;

  const { translations, translations_indexes } = item;

  const lang = language || i18n.language || i18n.options.fallbackLng[0];
  const lang_index =
    translations_indexes.language && translations_indexes.language[lang]
      ? translations_indexes.language[lang][0]
      : translations_indexes.default.true[0];
  const lang_default = translations_indexes.default.true[0];
  const trans = translations[lang_index];
  const trans_default = translations[lang_default];

  if (trans && trans[attr]) {
    return trans[attr];
  }

  return trans_default[attr];
};

function getShortLongDesc(trans, attr) {
  if (!trans) return null;

  if (attr === "short") return trans.short_description || trans.description;

  return trans.description || trans.short_description;
}

export const getTranslatedDescription = (item, attr, language) => {
  if (!item) return null;

  const { translations, translations_indexes } = item;

  const lang = language || i18n.language || i18n.options.fallbackLng[0];
  const lang_index = translations_indexes.language[lang]
    ? translations_indexes.language[lang][0]
    : translations_indexes.default.true[0];
  const lang_default = translations_indexes.default.true[0];
  const trans = translations[lang_index];
  const trans_default = translations[lang_default];

  let trad = getShortLongDesc(trans, attr);

  if (!trad) trad = getShortLongDesc(trans_default, attr);

  return trad;
};

export const getElementByUuid = (uuid, elements) => {
  if (!uuid || !elements || !elements.data || elements.data.length <= 0)
    return null;

  const index = elements.indexes.uuid[uuid];

  if (!index) return null;

  return elements.data[index[0]];
};

export const getElementsByIndex = (uuid, elements, index_name) => {
  if (!uuid || !elements || !elements.data || elements.data.length <= 0)
    return [];

  const index = elements.indexes[index_name][uuid];

  if (!index) return [];

  return index.map((it) => elements.data[it]);
};

export const translationsFromKey = (key, langs) => {
  const translations = [];
  const translations_indexes = {
    default: { true: [0] },
    language: {},
  };

  langs.forEach((lang, index) => {
    translations.push({
      language: lang,
      name: i18n.t(key, { lng: lang }),
    });

    translations_indexes.language[lang] = [index];
  });

  return {
    translations,
    translations_indexes,
  };
};

export const inList = (playlists, uuid) => {
  const playlist = playlists.data[0];

  return playlist
    ? playlist.medias.some((media) => media.media_uuid === uuid)
    : false;
};

export const inPlaylists = (uuid, playlists) => {
  let in_playlists = false;

  if (playlists && playlists.data) {
    playlists.data.forEach((playlist) => {
      if (inPlaylist(playlist, uuid)) {
        in_playlists = true;
      }
    });
  }

  return in_playlists;
};

export const inPlaylist = (playlist, uuid) =>
  playlist.medias.some((media) => media.media_uuid === uuid);

export const sendDisplayClickEvent = (display, language, token) => {
  const endpoint = rootURL("/api/public/v1/events/display");
  const data = {
    display_uuid: display.uuid,
    language,
    smartlist_uuid: display.smartlist_uuid,
    url: getTranslatedAttr(display, "url", language),
    target_blank: display.target_blank,
  };

  fetch(endpoint, {
    method: "POST",
    body: JSON.stringify(data),
    headers: formatHeaders(token),
  });
};

export const getContentIcon = (item) => {
  let icon = "";
  switch (item.itemType) {
    case "video":
      icon = getIcon(item);
      break;
    case "smartlist":
      icon =
        item.type === "series" || item.type === "season"
          ? "weekend"
          : "folder_special";
      switch (item.type) {
        case "standard":
          icon = "folder_special";
          break;
        case "audio":
          icon = "queue_music";
          break;
        case "series":
        case "season":
          icon = "weekend";
          break;
      }
      break;
    case "display":
      icon = "video_library";
      break;
    case "casting":
      icon = "face";
      break;
  }
  if (item.type === "master") {
    icon = "auto_awesome_motion";
  }
  return icon;
};

export const hasPermission = (
  abilities = null,
  abi = null,
  nested = null,
  write = false,
) => {
  let hasPermission = false;

  if (!abilities) return false;

  const perm = abilities[abi];

  if (!perm) return false;

  if (typeof perm === "string") {
    hasPermission = perm;
  } else {
    Object.entries(perm)?.some(([key, value]) => {
      if (key === nested) {
        hasPermission = value;
        return true;
      }
    });
  }

  if (write && hasPermission) {
    return hasPermission === "write";
  }

  return !!hasPermission;
};

export const getTranslatedMetadata = (metadata, attr, lang_uuid) => {
  if (!metadata) return null;

  const { translations, translations_indexes } = metadata;
  const { language_uuid } = translations_indexes;

  const lang_default = translations_indexes.default.true[0];
  const lang_index = language_uuid[lang_uuid] && language_uuid[lang_uuid][0];
  const trans = translations[lang_index];
  const trans_default = translations[lang_default];

  if (trans && trans[attr]) {
    return trans[attr];
  }

  return trans_default[attr];
};

export const getTypedFilteredCastings = (castings) => {
  const filtered = {};
  castings?.forEach((c) => {
    if (filtered[c.type]) {
      filtered[c.type].push(c);
    } else {
      filtered[c.type] = [c];
    }
  });
  return filtered;
};

export const getFiltersIndexMedias = (medias) => {
  let filters = {};
  let sort = {};

  Object.keys(medias?.filters).forEach((key) => {
    filters = { ...filters, ...{ [key]: medias.filters[key].key } };
  });

  // no filter by status ? take all except archived
  if (!filters.status) filters.status = "not_archived";

  // take filter type with content_type
  if (filters.content_type) {
    filters.type = getType(filters.content_type);
  } else if (!filters.type) {
    filters.type = medias.filters?.type;
  }

  // difference hosted types
  switch (filters.type) {
    // video only
    case "hosted":
      if (filters?.content_type) filters.audio_only = false;
      break;
    // replay only
    case "replay":
      filters.replay = true;
      filters.type = "hosted";
      break;
    // audio only
    case "audio":
      filters.audio_only = true;
      filters.type = "hosted";
      break;
    default:
      break;
  }

  if (filters.restrictions) {
    filters[filters.restrictions] = true;
    delete filters.restrictions;
  }

  if (filters.elements) {
    filters[filters.elements] = true;
    delete filters.elements;
  }

  if (Object.keys(medias?.sort).length) {
    sort = {
      direction: medias.sort?.direction,
      field: medias.sort.column,
    };
  }

  delete filters.content_type;

  return {
    per_page: medias.page?.perPage || 10,
    page: medias.page?.current,
    filter: filters,
    order: sort,
    search: medias?.searchText || null,
  };
};

export const transformMediasIndex = (indexMedias) => {
  indexMedias?.data?.map((item) => {
    let restrictions = item.temporal_restriction ? "temporal_restriction" : "";
    restrictions =
      item.geoblock_mode !== "disabled"
        ? `${restrictions} geoblock_mode`
        : restrictions;

    let elements = "";

    elements = item.series_count ? "series_count" : "";
    elements = item.free ? `${elements} free` : elements;
    elements = item.campaigns_count > 0 ? `${elements} sponsoring` : elements;
    item.elements = elements;

    item.duration_str = item.duration
      ? moment().startOf("day").seconds(item.duration).format("HH:mm:ss")
      : "";
    item.status =
      item.status === "archived" ? item.status : item.publication_status;
    item.pause_reasons = item.publication_reason || [];
    item.restrictions = restrictions || "";
    item.content_type = getIcon(item);
    return item;
  });

  return indexMedias;
};

export const computeAnalyticPreviewAndEmbeddedLinks = (content, projects) => {
  const { offers = [], uuid, translations } = content;

  const defaultSlug = getDefaultSlug(translations);

  const offersWithUniqProjects = offers.filter(
    (offer, index, array) =>
      array.findIndex(
        (arrayOffer) =>
          arrayOffer.storefront_project_name === offer.storefront_project_name,
      ) === index,
  );

  if (offersWithUniqProjects.length === 0)
    return { analyticLinks: null, previewLinks: null, embeddedItems: null };

  if (offersWithUniqProjects.length === 1) {
    const offer = offers[0];
    const isOfferFree = offer?.is_free;
    const baseURL = getStorefrontUrl(offer.storefront_project_uuid, projects);
    return {
      analyticLinks: `${PROJECTS_ROUTE}/${offer.storefront_project_name}/analytics/content/${uuid}`,
      previewLinks: `${baseURL}/content/${defaultSlug || uuid}`,
      embeddedItems: isOfferFree && getMediaEmbed({ baseURL, uuid }),
    };
  }

  const { analyticLinks, previewLinks, embeddedItems } =
    offersWithUniqProjects.reduce(
      (acc, offer) => {
        const { analyticLinks, previewLinks, embeddedItems } = acc;
        const baseURL = getStorefrontUrl(
          offer.storefront_project_uuid,
          projects,
        );

        analyticLinks.push({
          link: `${PROJECTS_ROUTE}/${offer.storefront_project_name}/analytics/content/${uuid}`,
          projectName: offer.storefront_project_name,
        });
        previewLinks.push({
          link: `${baseURL}/content/${defaultSlug || uuid}`,
          projectName: offer.storefront_project_name,
        });

        if (offer.is_free) {
          embeddedItems.push({
            projectName: offer.storefront_project_name,
            iframeCode: getMediaEmbed({ baseURL, uuid }),
          });
        }

        return acc;
      },
      { analyticLinks: [], previewLinks: [], embeddedItems: [] },
    );

  return { analyticLinks, previewLinks, embeddedItems };
};
